"use client"

import { useInView } from "react-intersection-observer"
import type { components } from "umbraco"
import { getMediaUrl, resolveLinkHref } from "umbraco"
import { getNonUmbracoUrl, replaceRichTextLinks } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import Container from "../../Container"
import GradientChevron from "../../icons/GradientChevron"
import ArrowLeft from "../../icons/ArrowLeft"
import { useTransitionUpStyles as transitionUpStyles } from "../../transitions/useTransitionUpStyles"
import { useInViewConfig, delayTime } from "../../transitions/utils"
import {
  Root,
  TitleRow,
  ChevronWrapper,
  Title,
  Subtitle,
  CardsGrid,
  Card,
  CardIcon,
  CardTitle,
  CardText,
  CardCta,
} from "./styles"

// EGDE-481: the advantagesBlock / advantageCard element types are newer than
// the generated schema (regen is gated, see
// feedback_type_regen_drops_missing_components), so they are typed here.
type AdvantageCardProps = {
  icon?: components["schemas"]["IApiMediaWithCropsModel"][] | null
  title?: string | null
  text?: string | null
  ctaLabel?: string | null
  ctaLink?: unknown
}

type AdvantagesBlockProps = {
  title?: { markup?: string | null } | string | null
  subtitle?: string | null
  cards?: {
    items?: Array<{
      content: { id?: string; properties?: Record<string, unknown> }
    }>
  } | null
}

interface Props {
  data: AdvantagesBlockProps
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  [key: string]: unknown
}

export default function AdvantagesBlock({ data }: Props) {
  // One-shot entrance: heading first, then the cards cascade in (EGDE-510
  // motion language).
  const { ref: entranceRef, inView } = useInView(useInViewConfig)

  // Title is CMS rich text — tolerate a legacy plain string too.
  const rawTitle = data.title
  const titleMarkup =
    rawTitle && typeof rawTitle === "object"
      ? replaceRichTextLinks(rawTitle.markup ?? "")
      : ""
  const titleText = typeof rawTitle === "string" ? rawTitle : ""
  const hasTitle = !!(titleMarkup.replace(/<[^>]*>/g, "").trim() || titleText)

  const cards = (data.cards?.items ?? [])
    .map((block, index) => {
      const props = block.content.properties as AdvantageCardProps | undefined
      if (!props) return null

      const iconRel = getMediaUrl(props.icon)
      const ctaResolved = resolveLinkHref(props.ctaLink, {
        getNonUmbracoUrl,
        getUmbracoMediaUrl,
      })
      return {
        id: block.content.id ?? `advantage-${index}`,
        iconSrc: iconRel ? getUmbracoMediaUrl(iconRel) : null,
        title: props.title ?? null,
        text: props.text ?? null,
        ctaLabel: props.ctaLabel ?? null,
        ctaUrl: ctaResolved?.href ?? null,
        ctaTarget: ctaResolved?.target,
      }
    })
    .filter(
      (card): card is NonNullable<typeof card> =>
        // Every field is optional — but a card with nothing at all renders
        // an empty white box, so it is skipped.
        !!card && !!(card.iconSrc || card.title || card.text || card.ctaLabel),
    )

  if (!hasTitle && !data.subtitle && cards.length === 0) return null

  return (
    <Container paddingMobile>
      <Root ref={entranceRef} data-block-type="advantagesBlock">
        {hasTitle && (
          <TitleRow style={transitionUpStyles(inView)}>
            <ChevronWrapper>
              <GradientChevron width={16} height={27} />
            </ChevronWrapper>
            {titleMarkup ? (
              <Title
                variant="h2"
                component="div"
                dangerouslySetInnerHTML={{ __html: titleMarkup }}
              />
            ) : (
              <Title variant="h2">{titleText}</Title>
            )}
          </TitleRow>
        )}

        {data.subtitle && (
          <Subtitle variant="body1" style={transitionUpStyles(inView)}>
            {data.subtitle}
          </Subtitle>
        )}

        {cards.length > 0 && (
          <CardsGrid>
            {cards.map((card, index) => (
              <Card
                key={card.id}
                style={transitionUpStyles(inView, delayTime + index * 0.08)}
              >
                {card.iconSrc && <CardIcon src={card.iconSrc} alt="" />}
                {card.title && (
                  <CardTitle variant="body1">{card.title}</CardTitle>
                )}
                {card.text && <CardText variant="body2">{card.text}</CardText>}
                {card.ctaLabel && card.ctaUrl && (
                  <CardCta
                    href={card.ctaUrl}
                    target={card.ctaTarget}
                    rel={
                      card.ctaTarget === "_blank"
                        ? "noopener noreferrer"
                        : undefined
                    }
                  >
                    {card.ctaLabel}
                    <ArrowLeft />
                  </CardCta>
                )}
              </Card>
            ))}
          </CardsGrid>
        )}
      </Root>
    </Container>
  )
}
